export enum CustomMergeChecksResourceType {
  Workspace = 'workspace',
  Project = 'project',
  Repository = 'repository',
}

export type CustomMergeChecksConfigApi = {
  getBranchConfigs: () => Promise<BranchConfigsResponse>;
  getCustomMergeChecks: () => Promise<CustomMergeChecksConfigurationResponse>;
};

export type CustomMergeChecksConfigureApi = {
  addCheck: (
    extensionId: CustomMergeCheckExtensionId,
    branch: BranchConfig,
    state: CustomMergeCheckConfigurationState
  ) => Promise<CustomMergeCheck>;
  editCheck: (
    uuid: string,
    state: CustomMergeCheckConfigurationState
  ) => Promise<CustomMergeCheck>;
  deleteCheck: (uuid: string) => Promise<undefined>;
};

// API response for fetching branch configs from bbc-checks
export type BranchConfigsResponse = BranchConfig[];

// API response for fetching custom merge checks
export type CustomMergeChecksConfigurationResponse = CustomMergeCheck[];

export type CustomMergeCheck = {
  uuid: string;
  extensionId: CustomMergeCheckExtensionId;
  state: CustomMergeCheckConfigurationState;
  resource: CustomMergeCheckResourceId;
  branchConfig: BranchConfig;
  app: {
    id: string;
    environmentId: string;
  };
};

// Data shape for customMergeChecksConfigRouteResource
export type CustomMergeCheckConfigRouteResource = {
  branchConfigs: BranchConfig[];
  enabledChecks: CustomMergeCheck[];
};

// Partial type (just what we use) of the Forge check module representation
export type ForgeMergeCheckModule = {
  properties: {
    // Check name
    name: string;
    description: string;
    triggers: CheckTrigger[];
  };
  id: CustomMergeCheckExtensionId;
  // App UUID
  appId: string;
  // Environment UUID
  environmentId: string;
  // App name
  name: string;
};

export type BranchConfig = {
  type: BranchConfigType;
  identifier: BranchConfigIdentifier;
};

export type CustomMergeCheckExtensionId = string;

// A workspace/project/repository ARI
export type CustomMergeCheckResourceId = string;

export type CheckTrigger =
  | 'on-merge'
  | 'on-code-pushed'
  | 'on-reviewer-status-changed';

export type CheckTriggerType = 'pre-merge' | 'on-merge';

export type BranchConfigType = 'model';

export type BranchConfigIdentifier = 'main' | 'all_branches';

export type CustomMergeCheckConfigurationState =
  | 'off'
  | 'recommended'
  | 'required';

export type ResultStatus =
  | 'ERROR'
  | 'FAILED'
  | 'IN-PROGRESS'
  | 'PENDING'
  | 'SUCCESS'
  | 'IMPENDING';

export type ForgeMergeCheckResult = {
  checkType: CheckTriggerType;
  trigger: CheckTrigger;
  id?: string;
  status: ResultStatus;
  updatedOn?: string;
  check: {
    name: string;
    id: string;
    app: {
      name: string;
      id: string;
    };
  };
  message?: string;
  branchConfig: BranchConfig;
  configUuid: string;
  enforcement: CustomMergeCheckResultEnforcement;
  resource: CustomMergeCheckResourceId;
};

export type CustomMergeCheckResultEnforcement = Exclude<
  CustomMergeCheckConfigurationState,
  'off'
>;

export type EnabledCustomOnMergeChecks = {
  error: Error | Record<string, any> | null;
  refresh: () => void;
  enabledChecks?: ForgeMergeCheckResult[];
};

// All possible router params for the various settings pages (repo/project/workspace)
export type CustomMergeCheckSettingsRouterParams = {
  repositoryOwner?: string;
  repositorySlug?: string;
  workspaceSlug?: string;
  projectKey?: string;
};
